/**
 * ProductDetailsConfig Component
 * Configures product-specific content generation settings like size mentions
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Package, Ruler, Info } from 'lucide-react';
import type { ProductDetailsConfig as ProductDetailsConfigType, SizeMentionMode, SizeMentionSection } from '@/hooks/useContentConfig';

interface ProductDetailsConfigProps {
  value: ProductDetailsConfigType;
  onChange: (value: ProductDetailsConfigType) => void;
  sourceLabel?: string;
}

const SIZE_MENTION_OPTIONS: { value: SizeMentionMode | 'default'; label: string; description: string; example: string }[] = [
  {
    value: 'default',
    label: 'Default (Include)',
    description: 'Include size information naturally in all content',
    example: '"Available in US 9, this shoe offers excellent support..."'
  },
  {
    value: 'include',
    label: 'Include Sizes',
    description: 'Actively include size information when relevant',
    example: '"The US 9 variant features the same premium materials..."'
  },
  {
    value: 'title_only',
    label: 'Title Only',
    description: 'Sizes only in product title, not in body content',
    example: '"Available in multiple sizes, this shoe offers excellent support..."'
  },
  {
    value: 'exclude',
    label: 'Never Mention',
    description: 'Never mention specific sizes in any content',
    example: '"This running shoe offers excellent support and cushioning..."'
  },
];

const SECTION_OPTIONS: { value: SizeMentionSection; label: string }[] = [
  { value: 'faq', label: 'FAQs' },
  { value: 'benefits', label: 'Benefits' },
  { value: 'use_cases', label: 'Use Cases' },
  { value: 'comparisons', label: 'Comparisons' },
  { value: 'summary', label: 'Summary' },
  { value: 'buyer_profiles', label: 'Buyer Profiles' },
  { value: 'recommendations', label: 'Recommendations' },
];

export function ProductDetailsConfig({ value, onChange, sourceLabel }: ProductDetailsConfigProps) {
  const currentMode = value.sizeMentionMode || 'default';
  const showSectionSelector = currentMode === 'title_only' || currentMode === 'exclude';

  const handleModeChange = (mode: string) => {
    if (mode === 'default') {
      onChange({ ...value, sizeMentionMode: null, sizeMentionSections: null });
    } else {
      onChange({ ...value, sizeMentionMode: mode as SizeMentionMode });
    }
  };

  const handleSectionToggle = (section: SizeMentionSection, checked: boolean) => {
    const currentSections = value.sizeMentionSections || [];
    let newSections: SizeMentionSection[];

    if (checked) {
      newSections = [...currentSections, section];
    } else {
      newSections = currentSections.filter(s => s !== section);
    }

    // If all sections are selected, set to null (means all)
    if (newSections.length === SECTION_OPTIONS.length) {
      onChange({ ...value, sizeMentionSections: null });
    } else if (newSections.length === 0) {
      // If none selected, default to all sections
      onChange({ ...value, sizeMentionSections: null });
    } else {
      onChange({ ...value, sizeMentionSections: newSections });
    }
  };

  const isSectionSelected = (section: SizeMentionSection): boolean => {
    // null means all sections
    if (!value.sizeMentionSections) return true;
    return value.sizeMentionSections.includes(section);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-orange-500" />
            <CardTitle className="text-lg">Product Details</CardTitle>
          </div>
          {sourceLabel && (
            <Badge variant="outline" className="text-xs">
              From: {sourceLabel}
            </Badge>
          )}
        </div>
        <CardDescription>
          Control how product-specific information like sizes appears in generated content
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Size Mention Mode */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Ruler className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Size Mention Mode</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            For products with sizes (e.g., "Puma Palermo US 9"), control whether the size is mentioned in body content
          </p>
          <RadioGroup
            value={currentMode}
            onValueChange={handleModeChange}
            className="space-y-3"
          >
            {SIZE_MENTION_OPTIONS.map((opt) => (
              <div key={opt.value} className="flex items-start space-x-3">
                <RadioGroupItem value={opt.value} id={`size-mode-${opt.value}`} className="mt-1" />
                <Label htmlFor={`size-mode-${opt.value}`} className="flex flex-col cursor-pointer flex-1">
                  <span className="font-medium">{opt.label}</span>
                  <span className="text-xs text-muted-foreground">{opt.description}</span>
                  <span className="text-xs text-muted-foreground/70 italic mt-1">
                    Example: {opt.example}
                  </span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Affected Sections (only shown when mode is title_only or exclude) */}
        {showSectionSelector && (
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Affected Sections</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Select which content sections should follow the size restriction. Leave all selected to apply to all sections.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {SECTION_OPTIONS.map((section) => (
                <div key={section.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`section-${section.value}`}
                    checked={isSectionSelected(section.value)}
                    onCheckedChange={(checked) => handleSectionToggle(section.value, checked as boolean)}
                  />
                  <Label
                    htmlFor={`section-${section.value}`}
                    className="text-sm cursor-pointer"
                  >
                    {section.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Alert */}
        {(currentMode === 'title_only' || currentMode === 'exclude') && (
          <Alert className="bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm">
              {currentMode === 'title_only' ? (
                <>
                  <strong>Title Only:</strong> Product titles will still contain size info (e.g., "Puma Palermo US 9"),
                  but FAQs, benefits, and other body content will avoid specific size mentions.
                  This prevents confusion when the page shows multiple size variants.
                </>
              ) : (
                <>
                  <strong>Never Mention:</strong> Size information will be completely omitted from all content.
                  Use this when sizes are irrelevant or handled separately (e.g., via size selectors).
                </>
              )}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
